import { apiClient, buildQueryParams } from './client';
import {
  CustomQueryParameters,
  OrderViewModel,
  Product,
  Service,
  TeamMemberViewModel,
} from '@/types';

export interface AssignWorkerInput {
  orderId: number;
  workerId?: number;
  driverId?: number;
}

export interface AddTeamMemberInput {
  fullName: string;
  phone: string;
  workerRole: number;
  specialtyIds: number[];
}

/** Tier 2.6 — partial update payload. Only fields with values are applied. */
export interface UpdateTeamMemberInput {
  ownerUserId: string;
  fullName?: string;
  workerRole?: number;
  isActive?: boolean;
  vehicleType?: string;
  vehiclePlate?: string;
}

export type ProviderExposureMode = 'hidden' | 'marketplace_only' | 'direct_only' | 'hybrid';
export type OrderAcceptanceMode = 'direct_to_provider' | 'first_accept' | 'quotation';

export interface ProviderBusinessExposure {
  id: number;
  providerId: number;
  specialtyId: number;
  specialtyNameAr: string;
  specialtyNameEn: string;
  moduleKey: string;
  exposureMode: number;
  enableMarketplace: boolean;
  enableDirectPage: boolean;
  showInSearch: boolean;
  showInCategories: boolean;
  enableBookingFlow: boolean;
  enableHomeServiceFlow: boolean;
  enableWorkerSelection: boolean;
  requiresDriver: boolean;
  acceptanceMode: number;
  autoConfirm: boolean;
}

export interface UpdateProviderBusinessExposureInput {
  providerUserId?: string;
  specialtyId: number;
  exposureMode: number;
  enableMarketplace: boolean;
  enableDirectPage: boolean;
  showInSearch: boolean;
  showInCategories: boolean;
  enableBookingFlow: boolean;
  enableHomeServiceFlow: boolean;
  enableWorkerSelection: boolean;
  requiresDriver: boolean;
  acceptanceMode: number;
  autoConfirm: boolean;
}

export interface ProviderZone {
  id: number;
  name: string;
  nameAr?: string;
  zoneType: number;
  districtId?: number;
  centerLatitude?: number;
  centerLongitude?: number;
  radiusKm?: number;
  geoJsonBoundary?: string;
  isActive: boolean;
  priority?: number;
  workerCount: number;
}

export interface ProviderDispatchTask {
  id: number;
  providerId: number;
  referenceId: number;
  referenceType: number;
  referenceTypeArabic: string;
  serviceWorkerId?: number;
  serviceWorkerName?: string;
  driverWorkerId?: number;
  driverWorkerName?: string;
  status: number;
  statusArabic: string;
  driverStatus?: number;
  dropoffAddress: string;
  dropoffLatitude: number;
  dropoffLongitude: number;
  scheduledFor?: string;
  estimatedArrival?: string;
  createdOn: string;
  completedOn?: string;
  matchedZoneId?: number;
  matchedZoneName?: string;
  proofCount: number;
  /** BZ2.5: other active crew on the assigned driver's vehicle (driver excluded). */
  crew?: TaskCrewMember[];
}

export interface TaskCrewMember {
  workerProviderId: number;
  workerName: string;
  /** WorkerRoleEnum: ServiceWorker=0, Driver=1, ServiceAndDriver=2. */
  role: number;
  roleArabic: string;
}

export interface CreateProviderDispatchTaskInput {
  referenceId: number;
  referenceType: number;
  serviceWorkerId?: number;
  driverWorkerId?: number;
  dropoffLatitude: number;
  dropoffLongitude: number;
  dropoffAddress: string;
  pickupLatitude?: number;
  pickupLongitude?: number;
  pickupAddress?: string;
  scheduledFor?: string;
}

export interface CreateProviderZoneInput {
  name: string;
  nameAr?: string;
  zoneType: number;
  districtId?: number;
  centerLatitude?: number;
  centerLongitude?: number;
  radiusKm?: number;
  geoJsonBoundary?: string;
}

export interface UpdateProviderZoneInput extends CreateProviderZoneInput {
  zoneId: number;
  isActive: boolean;
  priority: number;
}

export interface WorkerZoneAssignment {
  id: number;
  zoneId: number;
  workerProviderId: number;
  workerName: string;
  isPrimary: boolean;
  priority: number;
}

export interface AssignWorkerToZoneInput {
  zoneId: number;
  workerProviderId: number;
  isPrimary: boolean;
  priority: number;
}

export interface SaveProviderServiceInput {
  id?: number;
  specialtyId: number;
  subSpecialtyId: number;
  name: string;
  description?: string;
  price: number;
  orderPriority?: number;
  minimumQuantity?: number;
  maximumQuantity?: number;
  durationMinutes?: number;
  imagePath?: string;
  isActive?: boolean;
  serviceType?: number;
}

export interface SaveProviderProductInput {
  id?: number;
  specialtyId: number;
  subSpecialtyId: number;
  name: string;
  description?: string;
  price: number;
  stockQuantity: number;
  imagePath?: string;
  isActive?: boolean;
  providerUserId: string;
  providerId: number;
}

export interface ProviderShopOrderItem {
  id: number;
  productName: string;
  variantName?: string;
  productImage?: string;
  unitPrice: number;
  quantity: number;
  totalPrice: number;
}

export interface ProviderShopOrder {
  id: number;
  orderNumber: string;
  customerName: string;
  customerImage?: string;
  status: number;
  statusArabic: string;
  fulfillmentType: number;
  totalAmount: number;
  itemCount: number;
  paymentMethod: number;
  paymentStatus: number;
  createdOn: string;
  items?: ProviderShopOrderItem[];
}

export enum ShopOrderStatus {
  Placed = 0,
  PaymentPending = 1,
  Confirmed = 2,
  Preparing = 3,
  ReadyForShipment = 4,
  ReadyForPickup = 5,
  Shipped = 6,
  OutForDelivery = 7,
  Delivered = 8,
  Cancelled = 9,
  Returned = 10,
}

export enum ShopFulfillmentType {
  Delivery = 0,
  Pickup = 1,
}

export enum ShopPaymentMethod {
  Cash = 0,
  Electronic = 1,
}

export interface UpdateProviderShopOrderStatusInput {
  shopOrderId: number;
  newStatus: number;
  trackingNumber?: string;
  cancellationReason?: string;
}

export interface ProviderProductVariant {
  id: number;
  productId: number;
  sku: string;
  name?: string;
  nameAr?: string;
  price: number;
  compareAtPrice?: number;
  stockQuantity: number;
  reservedQuantity: number;
  availableStock: number;
  trackInventory: boolean;
  imagePath?: string;
  isActive: boolean;
}

export interface AddProviderProductVariantInput {
  providerUserId: string;
  productId: number;
  sku?: string;
  name?: string;
  nameAr?: string;
  price: number;
  compareAtPrice?: number;
  stockQuantity: number;
  trackInventory?: boolean;
  imagePath?: string;
}

export interface AdjustProviderInventoryInput {
  providerUserId: string;
  productVariantId: number;
  newQuantity: number;
  notes?: string;
}

export const providerAdminApi = {
  getOrders: async (params: CustomQueryParameters & { combinedStatusId?: number }): Promise<OrderViewModel[]> => {
    const query = buildQueryParams(params);
    const response = await apiClient.get<OrderViewModel[]>(`/providers/orders${query}`);
    return response.data;
  },

  getTeamMembers: async (userId: string, specialtyId?: number): Promise<TeamMemberViewModel[]> => {
    const params = buildQueryParams({ userId, specialtyId });
    const response = await apiClient.get<TeamMemberViewModel[]>(`/providers/team${params}`);
    return response.data;
  },

  getAvailableWorkers: async (userId: string, specialtyId?: number, forDate?: string): Promise<TeamMemberViewModel[]> => {
    const params = buildQueryParams({ userId, specialtyId, forDate });
    const response = await apiClient.get<TeamMemberViewModel[]>(`/providers/team/available${params}`);
    return response.data;
  },

  addTeamMember: async (data: AddTeamMemberInput): Promise<number> => {
    const response = await apiClient.post<number>('/providers/team', data);
    return response.data;
  },

  assignWorker: async (data: AssignWorkerInput): Promise<boolean> => {
    const response = await apiClient.post<boolean>('/providers/team/assign', data);
    return response.data;
  },

  /** Tier 2.6 — update a team member. Backend rejects unless the
   * caller's ownerUserId matches the worker's ParentProviderId.userId. */
  updateTeamMember: async (memberId: number, data: UpdateTeamMemberInput): Promise<boolean> => {
    const response = await apiClient.put<boolean>(`/providers/team/${memberId}`, data);
    return response.data;
  },

  /** Tier 2.6 — soft-deactivate (status → BLOCKED). Preserves the row. */
  deactivateTeamMember: async (memberId: number, ownerUserId: string): Promise<boolean> => {
    const response = await apiClient.delete<boolean>(
      `/providers/team/${memberId}?userId=${encodeURIComponent(ownerUserId)}`,
    );
    return response.data;
  },

  getBusinessExposures: async (): Promise<ProviderBusinessExposure[]> => {
    const response = await apiClient.get<ProviderBusinessExposure[]>('/providers/business/exposure');
    return response.data;
  },

  updateBusinessExposure: async (data: UpdateProviderBusinessExposureInput): Promise<ProviderBusinessExposure> => {
    const response = await apiClient.post<ProviderBusinessExposure>('/providers/business/exposure', data);
    return response.data;
  },

  getZones: async (): Promise<ProviderZone[]> => {
    const response = await apiClient.get<ProviderZone[]>('/providers/zones');
    return response.data;
  },

  createZone: async (data: CreateProviderZoneInput): Promise<number> => {
    const response = await apiClient.post<number>('/providers/zones', data);
    return response.data;
  },

  updateZone: async (data: UpdateProviderZoneInput): Promise<boolean> => {
    const response = await apiClient.put<boolean>('/providers/zones', data);
    return response.data;
  },

  /// `hard=false` (default) flips IsActive=false. `hard=true` drops the row + cascades worker assignments.
  /// Either path 4xx's when the zone has live dispatch tasks.
  deleteZone: async (zoneId: number, hard = false): Promise<boolean> => {
    const response = await apiClient.delete<boolean>(`/providers/zones/${zoneId}?hard=${hard}`);
    return response.data;
  },

  getZoneAssignments: async (zoneId: number): Promise<WorkerZoneAssignment[]> => {
    const response = await apiClient.get<WorkerZoneAssignment[]>(`/providers/zones/${zoneId}/assignments`);
    return response.data;
  },

  assignWorkerToZone: async (data: AssignWorkerToZoneInput): Promise<number> => {
    const response = await apiClient.post<number>('/providers/zones/assignments', data);
    return response.data;
  },

  unassignWorkerFromZone: async (assignmentId: number): Promise<boolean> => {
    const response = await apiClient.delete<boolean>(`/providers/zones/assignments/${assignmentId}`);
    return response.data;
  },

  getDispatchTasks: async (params?: { status?: number; page?: number; pageSize?: number }): Promise<ProviderDispatchTask[]> => {
    const query = buildQueryParams(params || {});
    const response = await apiClient.get<ProviderDispatchTask[]>(`/providers/dispatch/tasks${query}`);
    return response.data;
  },

  createDispatchTask: async (data: CreateProviderDispatchTaskInput): Promise<number> => {
    const response = await apiClient.post<number>('/providers/dispatch/tasks', data);
    return response.data;
  },

  getServicesBySpecialty: async (specialtyId: number): Promise<Service[]> => {
    const response = await apiClient.get<Service[]>(`/providers/services/specialty/${specialtyId}`);
    return response.data;
  },

  saveService: async (data: SaveProviderServiceInput): Promise<boolean> => {
    const response = await apiClient.post<boolean>('/providers/services', {
      id: data.id ?? 0,
      specialtyId: data.specialtyId,
      subSpecialtyId: data.subSpecialtyId,
      name: data.name,
      description: data.description,
      price: data.price,
      orderPriority: data.orderPriority ?? 0,
      minimumQuantity: data.minimumQuantity ?? 1,
      maximumQuantity: data.maximumQuantity ?? 1,
      durationMinutes: data.durationMinutes,
      imagePath: data.imagePath,
      isActive: data.isActive ?? true,
      serviceType: data.serviceType ?? 0,
    });
    return response.data;
  },

  deleteService: async (serviceId: number): Promise<boolean> => {
    const response = await apiClient.delete<boolean>(`/providers/services/${serviceId}`);
    return response.data;
  },

  getProductsBySpecialty: async (userId: string, specialtyId: number, page = 1): Promise<Product[]> => {
    void userId;
    const query = buildQueryParams({ specialtyId, page });
    const response = await apiClient.get<Product[]>(`/products/providers${query}`);
    return response.data;
  },

  saveProduct: async (data: SaveProviderProductInput): Promise<number | boolean> => {
    if (data.id) {
      const response = await apiClient.put<boolean>(`/products/providers/${data.id}`, {
        id: data.id,
        specialtyId: data.specialtyId,
        subSpecialtyId: data.subSpecialtyId,
        name: data.name,
        description: data.description,
        price: data.price,
        imagePath: data.imagePath,
        stockQuantity: data.stockQuantity,
        isActive: data.isActive ?? true,
      });
      return response.data;
    }

    const response = await apiClient.post<number>('/products/providers', {
      specialtyId: data.specialtyId,
      subSpecialtyId: data.subSpecialtyId,
      name: data.name,
      description: data.description,
      price: data.price,
      imagePath: data.imagePath,
      stockQuantity: data.stockQuantity,
      isActive: data.isActive ?? true,
    });
    return response.data;
  },

  deleteProduct: async (productId: number): Promise<boolean> => {
    const response = await apiClient.delete<boolean>(`/products/providers/${productId}`);
    return response.data;
  },

  getShopOrders: async (status?: number, page = 1, pageSize = 50): Promise<ProviderShopOrder[]> => {
    const query = buildQueryParams({ status, page, pageSize });
    const response = await apiClient.get<ProviderShopOrder[]>(`/commerce/providers/orders${query}`);
    return response.data;
  },

  updateShopOrderStatus: async (data: UpdateProviderShopOrderStatusInput): Promise<boolean> => {
    const response = await apiClient.post<boolean>('/commerce/providers/orders/status', data);
    return response.data;
  },

  getProductVariants: async (productId: number): Promise<ProviderProductVariant[]> => {
    const response = await apiClient.get<ProviderProductVariant[]>(`/commerce/providers/products/${productId}/variants`);
    return response.data;
  },

  addProductVariant: async (data: AddProviderProductVariantInput): Promise<number> => {
    const response = await apiClient.post<number>('/commerce/providers/products/variants', {
      productId: data.productId,
      sku: data.sku,
      name: data.name,
      nameAr: data.nameAr,
      price: data.price,
      compareAtPrice: data.compareAtPrice,
      stockQuantity: data.stockQuantity,
      trackInventory: data.trackInventory ?? true,
      imagePath: data.imagePath,
    });
    return response.data;
  },

  adjustInventory: async (data: AdjustProviderInventoryInput): Promise<boolean> => {
    const response = await apiClient.post<boolean>('/commerce/providers/inventory/adjust', {
      productVariantId: data.productVariantId,
      newQuantity: data.newQuantity,
      notes: data.notes,
    });
    return response.data;
  },

  // Phase 1.2 — manual order entry from owner's "+ New Order" button.
  // Backend resolves customer by phone (creates placeholder if new), attaches
  // an OrderDetail row per item, and respects ScheduledFor.
  createWalkInBooking: async (data: WalkInBookingInput): Promise<number> => {
    const response = await apiClient.post<number>('/booking/providers/walk-in', data);
    return response.data;
  },

  // Phase 2.2 — owner accepts a pending order. Auto-assignment runs with the
  // ownerAccepted bypass so AdminFirst providers actually get a worker pick.
  // When workerId is set, skips auto-assign and uses the explicit worker.
  acceptOwnerOrder: async (input: OwnerAcceptInput): Promise<OwnerAcceptResult> => {
    const response = await apiClient.post<OwnerAcceptResult>('/booking/providers/orders/owner-accept', input);
    return response.data;
  },

  rejectOwnerOrder: async (input: OwnerRejectInput): Promise<boolean> => {
    const response = await apiClient.post<boolean>('/booking/providers/orders/owner-reject', input);
    return response.data;
  },

  // Phase 2.4 — fetch the public tracking URL so the owner can copy it
  // and share with the customer via WhatsApp/SMS until automated
  // notifications cover walk-in customers (no Identity user yet).
  getOrderTrackingLink: async (orderId: number): Promise<OrderTrackingLink> => {
    const response = await apiClient.get<OrderTrackingLink>(`/booking/providers/orders/${orderId}/tracking-link`);
    return response.data;
  },

  // Phase 3.F — bulk-import customers parsed from a CSV.
  bulkImportCustomers: async (input: BulkImportCustomersInput): Promise<BulkImportCustomersResult> => {
    const response = await apiClient.post<BulkImportCustomersResult>(
      '/booking/providers/customers/bulk-import',
      input,
    );
    return response.data;
  },

  // Phase 3.K — vehicle management.
  listVehicles: async (providerUserId: string): Promise<Vehicle[]> => {
    const r = await apiClient.get<Vehicle[]>('/booking/providers/vehicles', {
      params: { providerUserId },
    });
    return r.data;
  },
  upsertVehicle: async (input: UpsertVehicleInput): Promise<Vehicle> => {
    const r = await apiClient.post<Vehicle>('/booking/providers/vehicles', input);
    return r.data;
  },
  deleteVehicle: async (vehicleId: number, providerUserId: string): Promise<boolean> => {
    const r = await apiClient.delete<boolean>(`/booking/providers/vehicles/${vehicleId}`, {
      params: { providerUserId },
    });
    return r.data;
  },
  // BZ2: crew CRUD per vehicle.
  listVehicleCrew: async (
    vehicleId: number,
    providerUserId: string,
    includeHistory = false,
  ): Promise<VehicleCrewMember[]> => {
    const r = await apiClient.get<VehicleCrewMember[]>(
      `/booking/providers/vehicles/${vehicleId}/crew`,
      { params: { providerUserId, includeHistory } },
    );
    return r.data;
  },
  addVehicleCrew: async (input: AddVehicleCrewInput): Promise<VehicleCrewMember> => {
    const r = await apiClient.post<VehicleCrewMember>(
      `/booking/providers/vehicles/${input.vehicleId}/crew`,
      input,
    );
    return r.data;
  },
  removeVehicleCrew: async (
    vehicleId: number,
    assignmentId: number,
    providerUserId: string,
  ): Promise<boolean> => {
    const r = await apiClient.delete<boolean>(
      `/booking/providers/vehicles/${vehicleId}/crew/${assignmentId}`,
      { params: { providerUserId } },
    );
    return r.data;
  },
  // BZ5: get-or-create the public tracking link for an order. Idempotent —
  // returns the same code on subsequent calls. Provider must own the order.
  getOrCreateOrderTrackingLink: async (
    orderId: number,
    providerUserId: string,
  ): Promise<TrackingLink> => {
    const r = await apiClient.get<TrackingLink>(`/orders/${orderId}/tracking-link`, {
      params: { providerUserId },
    });
    return r.data;
  },
};

// Note (RF2, 2026-05-24): driver scorecards + cash reconciliation API methods
// were briefly defined here but are duplicates of the canonical `analyticsApi`
// in `@/api/analytics`. Use `analyticsApi.getDrivers` / `analyticsApi.getCash`
// instead. Both take a numeric `providerId` (read from `useAuthStore`) — the
// rest of the analytics endpoints follow the same convention.

// ── Public tracking (BZ5, 2026-05-24) ──

export interface TrackingLink {
  code: string;
  /** Relative path; frontend prepends window.location.origin. e.g. "/t/aB3kQ9mZ". */
  publicPath: string;
}

export interface PublicTrackingView {
  orderId: number;
  statusId: number;
  statusLabel: string;
  statusLabelArabic: string;
  customerFirstName: string;
  dropoffLatitude: number;
  dropoffLongitude: number;
  dropoffAddress?: string | null;
  estimatedArrival?: string | null;
  scheduledFor?: string | null;
  tenantId?: number | null;
}

// Note (RF2, 2026-05-24): driver scorecard + cash reconciliation types
// removed — they were duplicates of canonical types in `@/api/analytics`.
// Import from there instead.

export interface Vehicle {
  id: number;
  providerId: number;
  plateNumber: string;
  make?: string | null;
  model?: string | null;
  year?: number | null;
  color?: string | null;
  fuelType: number;
  odometerKm?: number | null;
  nextServiceOdometerKm?: number | null;
  nextServiceOn?: string | null;
  assignedDriverProviderId?: number | null;
  assignedDriverName?: string | null;
  /** BZ2: vehicle capacity. Null = no cap. */
  seats?: number | null;
  /** BZ2: count of active crew assignments. */
  crewCount: number;
  isActive: boolean;
  notes?: string | null;
  createdOn: string;
  serviceDue: boolean;
}

export interface UpsertVehicleInput {
  providerUserId: string;
  id?: number;
  plateNumber: string;
  make?: string;
  model?: string;
  year?: number;
  color?: string;
  fuelType: number;
  odometerKm?: number;
  nextServiceOdometerKm?: number;
  nextServiceOn?: string;
  assignedDriverProviderId?: number;
  seats?: number;
  isActive: boolean;
  notes?: string;
}

// ── Vehicle crew (BZ2, 2026-05-23) ──

export interface VehicleCrewMember {
  id: number;
  vehicleId: number;
  workerProviderId: number;
  workerName: string;
  /** Worker role values: ServiceWorker=0, Driver=1, ServiceAndDriver=2. */
  role: number;
  roleArabic: string;
  fromDate: string;
  toDate?: string | null;
  isActive: boolean;
}

export interface AddVehicleCrewInput {
  providerUserId: string;
  vehicleId: number;
  workerProviderId: number;
  role: number;
  fromDate?: string;
  toDate?: string;
}

export interface BulkImportCustomersInput {
  providerUserId: string;
  rows: { name?: string; mobileNo: string }[];
}

export interface BulkImportCustomersResult {
  inserted: number;
  updated: number;
  skipped: number;
  errors: { rowIndex: number; mobileNo: string; reason: string }[];
}

export interface OrderTrackingLink {
  url: string;
  token: string;
}

export interface OwnerAcceptInput {
  orderId: number;
  providerUserId: string;
  workerId?: number;
}

export interface OwnerAcceptResult {
  orderId: number;
  assignedWorkerId?: number | null;
  assignedDriverId?: number | null;
  dispatchTaskId?: number | null;
  outcome: string;
  reason?: string | null;
}

export interface OwnerRejectInput {
  orderId: number;
  providerUserId: string;
  reason: string;
}

export interface WalkInBookingInput {
  specialtyId: number;
  customerName: string;
  customerPhone: string;
  workerId?: number;
  driverId?: number;
  serviceId?: number; // legacy single-service shortcut; prefer items[]
  items?: { serviceId: number; quantity: number }[];
  scheduledFor?: string; // ISO; null = walk-in / immediate
  selectedTime?: string;
  addressId?: number;
  addressDescription?: string;
  addressLatitude?: number;
  addressLongitude?: number;
  notes?: string;
  overridePrice?: number;
}

// ─── Phase 3.CC — schedule exceptions (vacation / holiday / sick / etc) ───
export interface ScheduleExceptionInput {
  providerUserId: string;
  workerId: number;
  date: string;       // ISO
  type: number;       // ScheduleExceptionTypeEnum: 0=DayOff 1=Holiday 2=SpecialHours 3=SickLeave 4=Vacation
  startTime?: string; // HH:mm:ss — only for SpecialHours
  endTime?: string;
  reason?: string;
  reasonAr?: string;
}

export interface RescheduleBookingInput {
  orderId: number;
  newDate: string;       // ISO
  newStartTime: string;  // HH:mm:ss
  newWorkerId?: number;
  reason?: string;
}

export const bookingApi = {
  getWorkerSchedules: async (workerId: number): Promise<unknown[]> => {
    const r = await apiClient.get<unknown[]>(`/booking/providers/worker-schedules/${workerId}`);
    return r.data;
  },
  addScheduleException: async (input: ScheduleExceptionInput): Promise<number> => {
    const r = await apiClient.post<number>('/booking/providers/schedule-exceptions', input);
    return r.data;
  },
  // Provider-side contract derives the provider actor from the authenticated
  // JWT and enforces ownership/state/slot server-side.
  reschedule: async (input: RescheduleBookingInput): Promise<boolean> => {
    const r = await apiClient.post<boolean>('/booking/providers/reschedule', input);
    return r.data;
  },
};

// ─── Phase 3.EE — Returns triage ──────────────────────────────────────────
export interface ReturnRequestRow {
  id: number;
  shopOrderId: number;
  orderNumber?: string | null;
  customerUserId: string;
  customerName?: string | null;
  status: number;       // ReturnStatusEnum: 0=Requested 1=Approved 2=Rejected 3=Refunded
  reason: string;
  reasonAr?: string | null;
  refundAmount: number;
  adminNotes?: string | null;
  imagePath?: string | null;
  createdOn: string;
  processedOn?: string | null;
}

export interface UpdateReturnStatusInput {
  returnRequestId: number;
  newStatus: number;
  refundAmount?: number;
  adminNotes?: string;
}

export const returnsApi = {
  list: async (status?: number): Promise<ReturnRequestRow[]> => {
    const params = buildQueryParams({ status, page: 1, pageSize: 200 });
    const r = await apiClient.get<ReturnRequestRow[]>(`/commerce/providers/returns${params}`);
    return r.data;
  },
  updateStatus: async (input: UpdateReturnStatusInput): Promise<boolean> => {
    const r = await apiClient.post<boolean>('/commerce/providers/returns/status', input);
    return r.data;
  },
};
